/**
 * The read behind the inspector: the plan itself.
 *
 * Who is ON it comes from `GET /plan-assignment`, which is `api/assignmentQueries` — the clients
 * scope reads the same request as a list, so the two projections live together beside the endpoint
 * they share rather than one here and one there.
 *
 * ── The plan shares the BUILDER's cache key, and that is the point ───────────────────────────
 * `planTemplateFullQueryKey` from `entities/plan-draft`, not a key of this slice's own. The builder
 * invalidates that key on every save, so a trainer who edits a plan and comes back to the library
 * sees the pattern week they just authored rather than the one from before they left. A second key
 * would mean the inspector kept a stale plan until its own staleness window expired, and nothing in
 * either module would say why.
 *
 * ── `staleTime` is FIVE MINUTES here, not the builder's `Infinity` ───────────────────────────
 * The entity's own options set `staleTime: Infinity` and `structuralSharing: false`, and copying
 * either would be copying a POLICY rather than a number. Both exist because the builder OWNS the
 * draft it loaded: a refetch behind an open editor would replace the tree the trainer is editing,
 * and shared structure would let Immer's baseline follow an edit it is supposed to measure. This
 * screen owns nothing — it is read-only, and it re-reads what the builder writes — so it takes the
 * ordinary window the owner named on 3 September 2026 and the default sharing.
 *
 * The two options objects therefore differ, deliberately. What they must share is the KEY, which is
 * why the key builder is imported rather than restated.
 *
 * ── `lang` is a parameter of this endpoint, unlike the library's ─────────────────────────────
 * `GET /plan-template/{id}/full` takes `?lang=` and returns server-localised text with it, so the
 * language is in the key. `GET /plan-template/library` takes none and carries none — see
 * `planLibraryQueries` for why adding one "for consistency" would be wrong there.
 */

import { queryOptions, type QueryClient } from '@tanstack/react-query';

import { planTemplateFullQueryKey } from '~/entities/plan-draft';
import { getPlanTemplateIdFull } from '~/shared/api/generated/endpoints/plan-template/plan-template';

import { toInspectorView, type InspectorView } from '../model/inspectorView';
import type { TranslateKey } from '../model/planLibraryRow';
import type { ContentLanguage } from '~/shared/i18n/contentLanguage';

/** The owner's window for a read-only preview, 3 September 2026. */
const INSPECTOR_STALE_TIME = 5 * 60 * 1000;

/**
 * The read itself, without a projection — what the observer and the prefetch queue have in common.
 *
 * The queue must warm the entry the observer will subscribe to, so the key, the request and the
 * window are written once. Only the `select` differs, and a prefetch has none: it stores the
 * RESPONSE, and the projection runs when a component subscribes.
 */
function planRead(planId: number, lang: ContentLanguage) {
  return {
    queryKey: planTemplateFullQueryKey(planId, lang),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getPlanTemplateIdFull(planId, { lang }, undefined, signal),
    staleTime: INSPECTOR_STALE_TIME,
  };
}

export function inspectorQueryOptions(planId: number, lang: ContentLanguage, t: TranslateKey) {
  return queryOptions({
    ...planRead(planId, lang),
    refetchOnWindowFocus: false,
    select: (response): InspectorView => toInspectorView(response, t),
  });
}

/**
 * Warm one plan's cache entry without observing it.
 *
 * `prefetchQuery` rather than `fetchQuery`: a prefetch that fails is a prefetch that did nothing,
 * and the row the trainer actually points at reports its own failure through its own observer.
 */
export async function prefetchInspector(
  client: QueryClient,
  planId: number,
  lang: ContentLanguage,
): Promise<void> {
  await client.prefetchQuery(planRead(planId, lang));
}
